// Tiny combobox: type to filter, click to select. ~390 exercises in the
// list is fine to render without virtualization, but we cap display at
// 50 to keep the popover compact.

import { useEffect, useMemo, useRef, useState } from 'react';
import css from './ExercisePicker.module.css';

interface ExerciseItem {
  id: string;
  name: string | null;
  region: string | null;
  sets: number;
}

interface Props {
  exercises: ExerciseItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function ExercisePicker({ exercises, selectedId, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => exercises.find((e) => e.id === selectedId) ?? null,
    [exercises, selectedId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return exercises.slice(0, 50);
    return exercises
      .filter((e) => {
        const hay = `${e.name ?? ''} ${e.id} ${e.region ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [exercises, query]);

  // Close on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <div className={css.wrap} ref={wrapRef}>
      <button
        type="button"
        className={css.trigger}
        onClick={() => {
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <span className={css.triggerLabel}>
          {selected ? (selected.name ?? selected.id) : 'select an exercise'}
        </span>
        {selected && (
          <span className={css.triggerMeta}>
            {selected.sets.toLocaleString()} sets{selected.region ? ` · ${selected.region}` : ''}
          </span>
        )}
        <span className={css.triggerCaret}>▾</span>
      </button>
      {open && (
        <div className={css.popover}>
          <input
            ref={inputRef}
            className={css.search}
            type="text"
            placeholder="search exercises…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <ul className={css.list}>
            {filtered.length === 0 && (
              <li className={css.empty}>no matches</li>
            )}
            {filtered.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  className={`${css.option}${e.id === selectedId ? ` ${css.optionActive}` : ''}`}
                  onClick={() => {
                    onSelect(e.id);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <span className={css.optName}>{e.name ?? e.id}</span>
                  <span className={css.optMeta}>
                    {e.region && <span className={css.optRegion}>{e.region}</span>}
                    <span className={css.optSets}>{e.sets.toLocaleString()}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
